import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ROUTE_PATHS } from "../../lib/constants";
import { useServicesState } from "../services/useServicesState";
import { useInstallationsState } from "./useInstallations";

export interface UseRequireInstallationResult {
  /** Whether the installation check is still loading */
  isLoading: boolean;
  /** Whether the user has at least one installation */
  hasInstallation: boolean;
  /** Whether we should redirect to onboarding */
  shouldRedirectToOnboarding: boolean;
  /** Whether we should redirect to home (e.g., user on onboarding but has installation) */
  shouldRedirectToHome: boolean;
  /** The current location pathname */
  currentPath: string;
}

export interface UseRequireInstallationOptions {
  /** Paths that don't require installation (e.g., onboarding flow) */
  publicPaths?: string[];
  /** The onboarding base path */
  onboardingPath?: string;
}

const DEFAULT_PUBLIC_PATHS = [ROUTE_PATHS.ONBOARDING, ROUTE_PATHS.ONBOARDING_MAP_PRODUCTS, ROUTE_PATHS.ONBOARDING_SUCCESS];
const DEFAULT_ONBOARDING_PATH = ROUTE_PATHS.ONBOARDING;

/**
 * Hook to check installation status and determine redirect behavior.
 *
 * Usage:
 * ```tsx
 * const { isLoading, hasInstallation, shouldRedirectToOnboarding, shouldRedirectToHome } = useRequireInstallation();
 *
 * if (isLoading) return <Loader />;
 * if (shouldRedirectToOnboarding) return <Navigate to="/onboarding" />;
 * if (shouldRedirectToHome) return <Navigate to="/" />;
 *
 * return <ProtectedContent />;
 * ```
 */
export const useRequireInstallation = (options: UseRequireInstallationOptions = {}): UseRequireInstallationResult => {
  const { publicPaths = DEFAULT_PUBLIC_PATHS, onboardingPath = DEFAULT_ONBOARDING_PATH } = options;

  const [{ initialized, installations, loading }, , useInstallationMethods] = useInstallationsState();
  const [{ services, loading: servicesLoading }] = useServicesState();
  const location = useLocation();
  const [fetchStatus, setFetchStatus] = useState<"idle" | "fetching" | "done">("idle");
  const prevInstallationsRef = useRef(installations);

  const servicesReady = services.size > 0 && !servicesLoading;
  const currentPath = location.pathname;

  // Check if current path is a public path (doesn't require installation)
  const isPublicPath = publicPaths.some((path) => currentPath.startsWith(path));
  const isOnboardingPath = currentPath.startsWith(onboardingPath);

  useEffect(() => {
    // Only fetch once when initialized, services are ready, and installations haven't been loaded yet
    if (
      initialized &&
      servicesReady &&
      !loading &&
      installations === undefined &&
      fetchStatus === "idle" &&
      useInstallationMethods?.fetch
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- idle → fetching transition gated on async-loaded installation state
      setFetchStatus("fetching");
      useInstallationMethods.fetch();
    }
  }, [initialized, servicesReady, loading, installations, fetchStatus, useInstallationMethods]);

  // Mark fetch as done when loading finishes after we triggered a fetch
  useEffect(() => {
    if (fetchStatus === "fetching" && !loading) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- fetching → done transition driven by external loading flag
      setFetchStatus("done");
    }
  }, [fetchStatus, loading]);

  // Reset fetch status when installations are cleared (e.g., vendor change)
  useEffect(() => {
    if (installations === undefined && fetchStatus === "done" && prevInstallationsRef.current !== undefined) {
      setFetchStatus("idle");
    }
    prevInstallationsRef.current = installations;
  }, [installations, fetchStatus]);

  // Determine loading state
  const isLoading =
    !initialized || !servicesReady || loading || fetchStatus === "fetching" || (fetchStatus === "idle" && installations === undefined);

  // Determine if user has installation
  const hasInstallation = !!(installations && installations.size > 0);

  // Determine redirect behavior
  let shouldRedirectToOnboarding = false;
  let shouldRedirectToHome = false;

  if (!isLoading) {
    if (!hasInstallation && !isPublicPath) {
      // User has no installation and is trying to access a protected route
      shouldRedirectToOnboarding = true;
    } else if (hasInstallation && isOnboardingPath) {
      // User has installation but is on onboarding - redirect to home
      shouldRedirectToHome = true;
    }
  }

  return {
    isLoading,
    hasInstallation,
    shouldRedirectToOnboarding,
    shouldRedirectToHome,
    currentPath,
  };
};
